import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { API_URL } from '../config/env';
import { createBearerJsonClient, setApiTokenProvider } from '../utils/apiFetchPolicy';
import { flushLocalUserStateToDb } from '../utils/userDataSync';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';

function getStoredAuth() {
  try {
    const s = localStorage.getItem(AUTH_USER_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function getStoredToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

// Register a global token provider so module-level api clients (and any other
// caller that builds a bearer client without an explicit token) send the auth
// header. setToken writes authToken to localStorage synchronously, so this is
// correct even when fetchAccountData runs in the same tick as login.
setApiTokenProvider(getStoredToken);

export const AccountDataContext = createContext(null);

export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        console.error('useAccountData must be used within an AccountDataProvider');
        // Return a default structure to prevent crashes during development
        return {
            setAuth: () => {},
            setToken: () => {},
            auth: null,
            token: null,
            email: null,
            password: null,
            addresses: [],
            setAddresses: () => {},
            paymentMethods: [],
            setPaymentMethods: () => {},
            accountDetails: null,
            setAccountDetails: () => {},
            fetchAccountData: () => {},
            isLoading: false,
            logout: () => {},
        };
    }
    return context;
};

const api = createBearerJsonClient(null, { baseURL: API_URL });

export const AccountDataProvider = ({ children }) => {

    const [auth, setAuthState] = useState(getStoredAuth);
    const [token, setTokenState] = useState(getStoredToken);

    const setAuth = useCallback((user) => {
        setAuthState(user);
        if (!user) {
            setTokenState(null);
        }
        try {
            if (user) {
                localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
            } else {
                localStorage.removeItem(AUTH_USER_KEY);
                localStorage.removeItem(AUTH_TOKEN_KEY);
            }
        } catch (e) {
            console.warn('AccountContext: could not persist auth', e);
        }
    }, []);

    const setToken = useCallback((t) => {
        setTokenState(t);
        try {
            if (t) {
                localStorage.setItem(AUTH_TOKEN_KEY, t);
            } else {
                localStorage.removeItem(AUTH_TOKEN_KEY);
                // Drop any cached membership entitlement on logout so a different
                // account signing in on the same browser can't inherit it.
                localStorage.removeItem('lc_membership_plan');
            }
        } catch (e) {
            console.warn('AccountContext: could not persist token', e);
        }
    }, []);
    const [email] = useState(null);
    const [password] = useState(null);

    /**
     * Sign out — the single source of truth for "log this browser out".
     *
     * Before this existed every surface rolled its own logout: some only
     * removed `authToken`, some only cleared the React state, and the account
     * dashboard bounced the visitor to /login (which looked like "nothing
     * happened" because the dashboard header was still rendered on the way out).
     *
     * Now logout always:
     *   1. tells the backend (best effort — the endpoint exists so the session
     *      can be invalidated server-side too);
     *   2. removes every persisted credential / entitlement cache;
     *   3. clears the in-memory session so the header flips to "Sign in".
     */
    const logout = useCallback(async () => {
        // Capture the token BEFORE clearing it — the server call below needs it.
        const current = getStoredToken();
        // Clear the browser session FIRST (synchronous) so the UI flips to
        // "Sign in" immediately, then tell the backend in the background.
        try {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
            // Cached membership entitlement — must not survive a sign-out.
            localStorage.removeItem('lc_membership_plan');
        } catch (e) {
            console.warn('AccountContext: could not clear the session', e);
        }
        setTokenState(null);
        setAuthState(null);

        try {
            if (current) {
                await fetch(`${API_URL}/auth/logout`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${current}`,
                        'Content-Type': 'application/json',
                    },
                }).catch(() => {});
            }
        } catch (e) {
            console.warn('AccountContext: logout request failed', e);
        }
    }, []);
    
    // Account details state
    const [accountDetails, setAccountDetails] = useState(null);

    // Addresses shared between account page and checkout/payment
    const [addresses, setAddresses] = useState([]);

    // Payment methods shared between account page and checkout/payment
    const [paymentMethods, setPaymentMethods] = useState([]);
    
    const [isLoading, setIsLoading] = useState(false);

    // Resolve the server-authoritative membership entitlement + profile on login
    // / app mount so plan-gated material (curriculum / comps / prep) is correct
    // on first paint. Also backfill browser-only user state into the server DB.
    // Idempotent; failures are non-fatal.
    useEffect(() => {
        if (!token) return;
        if (process.env.NODE_ENV === 'test') return; // never hit the network during unit tests
        const userId = auth?._id || auth?.id;
        if (userId) {
            // Populates accountDetails.plan, which the plan gate reads. Runs on
            // mount + whenever the token changes (e.g. login). The dev proxy may
            // lack the backend, so failures are swallowed by the .catch below.
            fetchAccountData(userId).catch(() => {});
        }
        if (process.env.NODE_ENV === 'development') return; // dev proxy backend lacks /api/user/data; skip the 404-spamming backfill
        flushLocalUserStateToDb(token, userId).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);



    // Function to fetch account data from backend
    const fetchAccountData = async (userId) => {
        if (!userId) {
            // Reset to empty if no user
            setAddresses([]);
            setPaymentMethods([]);
            setAccountDetails(null);
            return;
        }

        setIsLoading(true);
        try {
            // Load addresses + payment methods directly from their list endpoints.
            // These return full objects in exactly the shape the UI expects and do
            // NOT depend on /auth/profile/:id, which is missing from the currently
            // deployed backend build and was 404'ing (so addresses/cards never loaded).
            const [addrRes, payRes] = await Promise.all([
                api.get('/auth/addresses').catch(() => null),
                api.get('/auth/payment-methods').catch(() => null),
            ]);

            if (addrRes?.data?.addresses && Array.isArray(addrRes.data.addresses)) {
                setAddresses(addrRes.data.addresses);
            } else {
                setAddresses([]);
            }

            if (payRes?.data?.paymentMethods && Array.isArray(payRes.data.paymentMethods)) {
                setPaymentMethods(payRes.data.paymentMethods);
            } else {
                setPaymentMethods([]);
            }

            // Best-effort: pull profile details (name/email/avatar). Optional for
            // checkout — if the profile route is unavailable we keep the
            // addresses/payment methods we already loaded instead of failing the call.
            try {
                const profileRes = await api.get(`/auth/profile/${userId}`);
                const profile = profileRes.data?.user;
                if (profile) {
                    /* Prefer uploaded avatar; backend may return defaultAvatarUrl for server-generated initials image */
                    const profileImageUrl = profile.profileImageUrl || profile.avatarUrl || profile.avatar || profile.defaultAvatarUrl;
                    const details = {
                        firstName: profile.firstName || profile.name?.split(' ')[0] || '',
                        lastName: profile.lastName || profile.name?.split(' ')[1] || '',
                        email: profile.email || '',
                        phone: profile.phone || '',
                        profileImageUrl,
                    };
                    setAccountDetails(details);
                    const profileId = profile._id || profile.id || userId;
                    setAuthState((prev) => {
                        if (!prev) return prev;
                        if ((prev._id || prev.id) === profileId) {
                            const next = { ...prev, profileImageUrl: details.profileImageUrl, avatarUrl: details.profileImageUrl };
                            try {
                                localStorage.setItem(AUTH_USER_KEY, JSON.stringify(next));
                            } catch (e) {
                                console.warn('AccountContext: could not persist profile image', e);
                            }
                            return next;
                        }
                        return prev;
                    });
                }
            } catch (profileError) {
                // Profile route may be unavailable on some deployments; not fatal.
                console.warn('AccountContext: profile details unavailable:', profileError?.message || profileError);
            }

            // Membership entitlement — SERVER-AUTHORITATIVE. Paid features must
            // gate on this, never on localStorage (which anyone can edit).
            try {
                const membershipRes = await api.get('/auth/membership');
                const membership = membershipRes?.data?.membership || null;
                setAccountDetails((prev) => ({
                    ...(prev || {}),
                    plan: membership
                        ? { id: membership.planId, interval: membership.interval }
                        : null,
                    membership,
                }));
            } catch (membershipError) {
                console.warn('AccountContext: membership unavailable:', membershipError?.message || membershipError);
            }
        } catch (error) {
            console.error('Error fetching account data:', error);
            setAddresses([]);
            setPaymentMethods([]);
        } finally {
            setIsLoading(false);
        }
    };

    const contextValue = {
        setAuth,
        setToken,
        auth,
        token,
        email,
        password,
        addresses,
        setAddresses,
        paymentMethods,
        setPaymentMethods,
        accountDetails,
        setAccountDetails,
        fetchAccountData,
        isLoading,
        logout,
    };

    return (
        <AccountDataContext.Provider value={contextValue}>
            {children}
        </AccountDataContext.Provider>
    );
};


